import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Monitor, Smartphone, HelpCircle, Users, Loader2, RefreshCw, Compass, Eye, BarChart3, TrendingUp, FileText, MousePointerClick, Target, Building2, Calculator, Database } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";

const PLATFORMS = [
  { key: "web", label: "Website", icon: Monitor, color: "#2563eb", bg: "bg-brandblue-light", text: "text-brandblue" },
  { key: "mobile", label: "App", icon: Smartphone, color: "#16a34a", bg: "bg-success/10", text: "text-success" },
  { key: "unknown", label: "Onbekend", icon: HelpCircle, color: "#94a3b8", bg: "bg-slate-100", text: "text-slate-500" },
];

const StatCard = ({ icon: Icon, label, value, sub, tint }) => (
  <div className="rounded-3xl border border-border bg-white p-6 shadow-sm" data-testid={`stat-${label}`}>
    <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tint}`}>
      <Icon size={20} />
    </div>
    <div className="mt-4 font-display text-3xl font-extrabold text-navy">{value}</div>
    <div className="mt-1 text-sm font-medium text-slate-500">{label}</div>
    {sub != null && <div className="mt-0.5 text-xs text-slate-400">{sub}</div>}
  </div>
);

const SplitBar = ({ web, mobile, unknown }) => {
  const total = web + mobile + unknown || 1;
  const seg = (n, color) => ({ width: `${(n / total) * 100}%`, backgroundColor: color });
  return (
    <div className="flex h-4 w-full overflow-hidden rounded-full bg-slate-100" data-testid="split-bar">
      <div style={seg(web, "#2563eb")} title={`Website: ${web}`} />
      <div style={seg(mobile, "#16a34a")} title={`App: ${mobile}`} />
      <div style={seg(unknown, "#cbd5e1")} title={`Onbekend: ${unknown}`} />
    </div>
  );
};

const Admin = () => {
  useSeo({ title: "Admin", description: "Admin dashboard" });
  const [data, setData] = useState(null);
  const [acq, setAcq] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [error, setError] = useState("");
  const [pendingApprovals, setPendingApprovals] = useState(0);

  const load = () => {
    setData(null);
    setAcq(null);
    setTraffic(null);
    setError("");
    api("/admin/platform-stats")
      .then(setData)
      .catch((e) => setError(e.message));
    api("/admin/acquisition-stats")
      .then(setAcq)
      .catch(() => {});
    api("/admin/traffic-stats")
      .then(setTraffic)
      .catch(() => {});
    api("/admin/makelaar-approvals?status=pending")
      .then((d) => setPendingApprovals(d?.counts?.pending || 0))
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <p className="text-slate-500" data-testid="admin-error">{error}</p>
        <Button onClick={load} className="mt-6 rounded-xl">Opnieuw</Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="animate-spin text-brandblue" size={32} />
      </div>
    );
  }

  const s = data.signup;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-navy" data-testid="admin-title">Web vs App</h1>
          <p className="mt-1 text-sm text-slate-500">Waar je gebruikers vandaan komen en wat ze gebruiken.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" className="rounded-xl" data-testid="admin-users-link">
            <Link to="/admin/users"><Users size={16} className="mr-2" /> Gebruikers</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-xl" data-testid="admin-broker-listings-link">
            <Link to="/admin/broker-listings"><Building2 size={16} className="mr-2" /> Makelaar listings</Link>
          </Button>
          <Button asChild variant={pendingApprovals > 0 ? "default" : "outline"} className={`rounded-xl ${pendingApprovals > 0 ? "bg-danger text-white hover:bg-danger/90" : ""}`} data-testid="admin-approvals-link">
            <Link to="/admin/makelaar-approvals">
              <Users size={16} className="mr-2" /> Goedkeuringen
              {pendingApprovals > 0 && (
                <span className="ml-2 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-bold tabular-nums" data-testid="approvals-badge">{pendingApprovals}</span>
              )}
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-xl" data-testid="admin-blog-link">
            <Link to="/admin/blog"><FileText size={16} className="mr-2" /> Blog</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-xl" data-testid="admin-behavior-link">
            <Link to="/admin/behavior"><MousePointerClick size={16} className="mr-2" /> Gedrag</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-xl" data-testid="admin-leads-link">
            <Link to="/admin/vastgoed-leads"><Target size={16} className="mr-2" /> Leads</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-xl" data-testid="admin-partners-link">
            <Link to="/admin/partners"><Building2 size={16} className="mr-2" /> Partners</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-xl" data-testid="admin-makelaar-feeds-link">
            <Link to="/admin/makelaar-feeds"><Database size={16} className="mr-2" /> Makelaar-feeds</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-xl" data-testid="admin-mortgage-link">
            <Link to="/admin/mortgage"><Calculator size={16} className="mr-2" /> Hypotheek</Link>
          </Button>
          <Button asChild className="rounded-xl bg-navy text-amber-300 hover:bg-navy/90" data-testid="admin-marketing-link">
            <Link to="/admin/marketing"><TrendingUp size={16} className="mr-2" /> Marketing & Growth</Link>
          </Button>
          <Button variant="outline" onClick={load} className="rounded-xl" data-testid="admin-refresh">
            <RefreshCw size={16} className="mr-2" /> Ververs
          </Button>
        </div>
      </div>

      {/* Website traffic — pageviews & unique visitors (cookieless, no Google) */}
      <div className="mt-8 rounded-3xl border border-border bg-white p-6 shadow-sm" data-testid="traffic-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-brandblue" />
            <h2 className="font-display text-lg font-bold text-navy">Websitebezoek</h2>
          </div>
          <Button asChild variant="outline" size="sm" className="rounded-xl" data-testid="analytics-diagnostics-link">
            <Link to="/admin/analytics-diagnostics">Diagnostics</Link>
          </Button>
        </div>
        <p className="mt-1 text-xs text-slate-400">
          Bezoekers en paginaweergaven van de website — anoniem gemeten, zonder cookies of Google. Unieke bezoekers zijn geschat op basis van een gepseudonimiseerd IP. Bots en /admin-paden tellen niet mee.
        </p>
        {traffic?.tracking_boundary && (
          <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="traffic-boundary">
            ⓘ Tracking verbeterd vanaf {new Date(traffic.tracking_boundary).toLocaleDateString("nl-NL")} (bot-filter, dedup &amp; admin-exclusie). Oudere cijfers kunnen hoger liggen door dubbeltelling.
          </div>
        )}
        {!traffic ? (
          <div className="mt-6 flex justify-center"><Loader2 className="animate-spin text-brandblue" size={22} /></div>
        ) : (
          <>
            <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard icon={Users} label="Bezoekers (30 dagen)" value={traffic.visitors.last_30d} sub={`Vandaag: ${traffic.visitors.today} · 7d: ${traffic.visitors.last_7d}`} tint="bg-brandblue-light text-brandblue" />
              <StatCard icon={Eye} label="Paginaweergaven (30 dagen)" value={traffic.pageviews.last_30d} sub={`Vandaag: ${traffic.pageviews.today} · 7d: ${traffic.pageviews.last_7d}`} tint="bg-success/10 text-success" />
              <StatCard
                icon={BarChart3}
                label="Pagina's per bezoeker (30d)"
                value={traffic.pages_per_visitor?.last_30d ?? "—"}
                sub={`Vandaag: ${traffic.pages_per_visitor?.today ?? "—"} · 7d: ${traffic.pages_per_visitor?.last_7d ?? "—"}`}
                tint="bg-amber-100 text-amber-700"
              />
              <StatCard icon={Users} label="Unieke bezoekers (totaal)" value={traffic.total_visitors} tint="bg-navy/10 text-navy" />
            </div>

            {traffic.daily && traffic.daily.length > 0 && (() => {
              const maxPv = Math.max(1, ...traffic.daily.map((d) => d.pageviews));
              return (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-navy">Laatste 14 dagen</h3>
                  <div className="mt-3 flex items-end gap-1.5" style={{ height: 120 }} data-testid="traffic-chart">
                    {traffic.daily.map((d) => (
                      <div key={d.date} className="flex flex-1 flex-col items-center justify-end" title={`${d.date}: ${d.pageviews} weergaven · ${d.visitors} bezoekers`}>
                        <div className="w-full rounded-t bg-brandblue" style={{ height: `${(d.pageviews / maxPv) * 100}%`, minHeight: d.pageviews > 0 ? 3 : 0 }} />
                        <span className="mt-1 text-[9px] text-slate-400">{d.date.slice(5)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {traffic.top_pages && traffic.top_pages.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-navy">Meest bezochte pagina&apos;s (30 dagen)</h3>
                <div className="mt-3 overflow-hidden rounded-xl border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Pagina</th>
                        <th className="px-3 py-2 text-right font-semibold">Pageviews</th>
                        <th className="px-3 py-2 text-right font-semibold">Unieke bezoekers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {traffic.top_pages.map((p) => (
                        <tr key={p.path} className="border-t border-border" data-testid={`traffic-page-${p.path}`}>
                          <td className="truncate px-3 py-2 font-medium text-navy">{p.path || "/"}</td>
                          <td className="px-3 py-2 text-right text-slate-500">{p.count}</td>
                          <td className="px-3 py-2 text-right text-slate-500">{p.unique_visitors ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Signup origin */}
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Users} label="Totaal gebruikers" value={data.total_users} tint="bg-navy/10 text-navy" />
        <StatCard icon={Monitor} label="Via website" value={s.web} sub={`${s.web_pct}% van totaal`} tint="bg-brandblue-light text-brandblue" />
        <StatCard icon={Smartphone} label="Via app" value={s.mobile} sub={`${s.mobile_pct}% van totaal`} tint="bg-success/10 text-success" />
        <StatCard icon={HelpCircle} label="Onbekend" value={s.unknown} sub={`${s.unknown_pct}% (nog niet ingelogd)`} tint="bg-slate-100 text-slate-500" />
      </div>

      {/* Split bar */}
      <div className="mt-8 rounded-3xl border border-border bg-white p-6 shadow-sm">
        <h2 className="font-display text-lg font-bold text-navy">Verdeling aanmeldingen</h2>
        <div className="mt-5">
          <SplitBar web={s.web} mobile={s.mobile} unknown={s.unknown} />
        </div>
        <div className="mt-4 flex flex-wrap gap-5 text-sm">
          {PLATFORMS.map((p) => (
            <span key={p.key} className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="font-semibold text-navy">{p.label}</span>
              <span className="text-slate-400">{s[p.key]}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Active + new */}
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div className="rounded-3xl border border-border bg-white p-6 shadow-sm">
          <h2 className="font-display text-lg font-bold text-navy">Actief (30 dagen)</h2>
          <p className="text-xs text-slate-400">Op basis van laatst gebruikte client</p>
          <div className="mt-4 space-y-3">
            {PLATFORMS.map((p) => (
              <div key={p.key} className="flex items-center justify-between" data-testid={`active-${p.key}`}>
                <span className="flex items-center gap-2 text-sm font-medium text-slate-600">
                  <p.icon size={16} /> {p.label}
                </span>
                <span className="font-display text-lg font-bold text-navy">{data.active_30d[p.key]}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl border border-border bg-white p-6 shadow-sm">
          <h2 className="font-display text-lg font-bold text-navy">Nieuw (7 dagen)</h2>
          <p className="text-xs text-slate-400">Nieuwe aanmeldingen per platform</p>
          <div className="mt-4 space-y-3">
            {PLATFORMS.map((p) => (
              <div key={p.key} className="flex items-center justify-between" data-testid={`new-${p.key}`}>
                <span className="flex items-center gap-2 text-sm font-medium text-slate-600">
                  <p.icon size={16} /> {p.label}
                </span>
                <span className="font-display text-lg font-bold text-navy">{data.new_7d[p.key]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Acquisition: where did they find us */}
      <div className="mt-6 rounded-3xl border border-border bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <Compass size={18} className="text-brandblue" />
          <h2 className="font-display text-lg font-bold text-navy">Waar komen ze vandaan</h2>
        </div>
        <p className="mt-1 text-xs text-slate-400">
          Eerste bron per gebruiker bij aanmelding (verwijzer + UTM). Alleen website-verkeer.
        </p>
        {!acq ? (
          <div className="mt-6 flex justify-center"><Loader2 className="animate-spin text-brandblue" size={22} /></div>
        ) : acq.channels.length === 0 ? (
          <p className="mt-6 text-sm text-slate-500">Nog geen brondata.</p>
        ) : (
          <>
            <div className="mt-2 text-xs text-slate-400" data-testid="acq-coverage">
              {acq.known} van {acq.total_users} gebruikers herleidbaar ({acq.known_pct}%)
            </div>
            <div className="mt-4 space-y-3">
              {acq.channels.map((c) => (
                <div key={c.name} data-testid={`acq-channel-${c.name}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-navy">{c.name}</span>
                    <span className="text-slate-400">{c.count} · {c.pct}%</span>
                  </div>
                  <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-brandblue" style={{ width: `${c.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
            {acq.top_campaigns.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-navy">Top campagnes (UTM)</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {acq.top_campaigns.map((c) => (
                    <span key={c.campaign} className="inline-flex items-center gap-1.5 rounded-full bg-brandblue-light px-3 py-1.5 text-sm font-semibold text-brandblue">
                      {c.campaign} <span className="text-brandblue/60">{c.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Admin;
